import { AuthenticationTriggerWrapper } from '@/components/buttons/AuthenticationTriggerWrapper';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { useAuthentication } from '@/hooks/useAuthentication';
import { getBidCurrencies } from '@/utils/getChainCurrencies';
import { ReservoirAction } from '@/utils/reservoir';
import { routes } from '@/utils/routes';
import { Button, ButtonProps } from '@chakra-ui/react';
import { BidModal, BidStep } from '@sphere/reservoir-kit-ui';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { type FC } from 'react';
import { toast } from 'sonner';
import ActionDisabledTooltip from './ActionDisabledTooltip';

type Props = ButtonProps & {
  tokenId?: string | undefined;
  collectionId?: string | undefined;
  disabled?: boolean;
  openState?: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  mutate?(): void;
};

const Bid: FC<Props> = ({ tokenId, collectionId, disabled, openState, mutate, ...buttonProps }) => {
  const router = useRouter();
  const { userAddress } = useAuthentication();
  const { t: tCommon } = useTranslation('common');
  const { t: tNft } = useTranslation('nft');
  const marketplaceChain = useMarketplaceChain();

  const Trigger = (
    <ActionDisabledTooltip action={ReservoirAction.Bid}>
      <Button flex="1 0 auto" disabled={disabled} variant="cta-secondary" {...buttonProps}>
        {tNft('purchase-controls.make-offer')}
      </Button>
    </ActionDisabledTooltip>
  );

  return (
    <AuthenticationTriggerWrapper trigger={Trigger}>
      <BidModal
        tokenId={tokenId}
        collectionId={collectionId}
        trigger={Trigger}
        openState={openState}
        currencies={getBidCurrencies(marketplaceChain.id, collectionId)}
        oracleEnabled={marketplaceChain.oracleBidsEnabled}
        chainId={marketplaceChain.id}
        onClose={(data, stepData, currentStep) => {
          if (mutate && currentStep == BidStep.Complete) mutate();
        }}
        onBidComplete={() => {
          toast.success(tCommon('toast.place-bid.success.title'), {
            description: tCommon('toast.place-bid.success.description'),
          });
        }}
        onBidError={error => {
          if (error) {
            if ((error as any).cause.code && (error as any).cause.code === 4001) {
              toast.error(tCommon('toast.general.transaction-cancelled.title'), {
                description: tCommon('toast.general.transaction-cancelled.description'),
              });
              return;
            }
          }
          toast.error(tCommon('toast.place-bid.transaction-incomplete.title'));
        }}
        onViewOffers={() =>
          router.push(
            routes.profile(userAddress ?? '', marketplaceChain.routePrefix, { tab: 'offers' }),
          )
        }
      />
    </AuthenticationTriggerWrapper>
  );
};

export default Bid;
